import { useEffect, useRef, useState } from 'react';
import { useAuthQuery as useQuery } from './useAuthQuery';
import { api } from '../../convex/_generated/api';
import { getBucketKey } from '../../logic';

interface InnerWeatherQuote {
  quote: string;
  author: string;
}

/**
 * Fetches quotes for the given dimension scores and picks a random one.
 * The selected quote is cached and only changes when the bucket key changes
 * (i.e., when scores cross a threshold boundary).
 */
export const useInnerWeatherQuote = (
  a: number,
  b: number,
  c: number
): InnerWeatherQuote | null => {
  const bucketKey = getBucketKey(a, b, c);
  const quotes = useQuery(api.inner_weather_quotes.getQuotesForScores, { a, b, c });
  const [selectedQuote, setSelectedQuote] = useState<InnerWeatherQuote | null>(null);
  const lastBucketKey = useRef<string>('');

  useEffect(() => {
    if (!quotes || quotes.length === 0) {
      setSelectedQuote(null);
      return;
    }

    // Only pick a new random quote when the bucket changes
    if (bucketKey !== lastBucketKey.current) {
      lastBucketKey.current = bucketKey;
      const randomIndex = Math.floor(Math.random() * quotes.length);
      const picked = quotes[randomIndex];
      setSelectedQuote({ quote: picked.quote, author: picked.author });
    }
  }, [quotes, bucketKey]);

  return selectedQuote;
};
